import { MeasurementSystem } from "src/js/stores/user/store";
import { filter, identity, map, path, pipe } from "ramda";
import { getMean, getMeanPerArea } from "./Averages";
import { squareFeetToSquareMeters } from "react-migration/lib/util/conversion";
import { ZooplaPriceModes, ZooplaPricePerAreaModes, ZooplaPricePerUnitModes } from "../typings/PriceModes";
import { ZooplaComparableDTO } from "react-migration/lib/typings/Zoopla";
import { CalculatorResultTypes } from "../typings/ResultTypes";

const averagesFilter: Record<ZooplaPriceModes, (transactions: ZooplaComparableDTO[]) => ZooplaComparableDTO[]> = {
  // Valuation PPA Sold -  Based on last sale value & floor area
  [ZooplaPricePerAreaModes.VAL_LAST]: filter<ZooplaComparableDTO>(
    (x) => !!x.valuation?.sale?.lastValue && !!x.attributes?.floorArea
  ),
  // Valuation PPU Sold - Based on last sale value
  [ZooplaPricePerUnitModes.VAL_LAST]: filter<ZooplaComparableDTO>((x) => !!x.valuation?.sale?.lastValue),
  // Rent Asking PPU Sold - Based on last rent value
  [ZooplaPricePerUnitModes.RENT_ASKING]: filter<ZooplaComparableDTO>(
    (x) => !!x.valuation?.rent?.lastValue && /asking/i.test(x.valuation?.rent?.lastValueType ?? "")
  ),
  // Rent Estimate PPU Sold - Based on last rent value
  [ZooplaPricePerUnitModes.RENT_ESTIMATE]: filter<ZooplaComparableDTO>((x) => !!x.valuation?.rent?.currentValue),
  // Valuation PPU Market - Based on last sale value
  [ZooplaPricePerUnitModes.VAL_ESTIMATE]: filter<ZooplaComparableDTO>((x) => !!x.valuation?.sale?.currentValue),
  // Rent Asking PPA Sold - Based on last rent value and floor area
  [ZooplaPricePerAreaModes.RENT_ASKING]: filter<ZooplaComparableDTO>(
    (x) =>
      !!x.valuation?.rent?.lastValue &&
      /asking/i.test(x.valuation?.rent?.lastValueType ?? "") &&
      !!x.attributes.floorArea
  ),
  // Rent Estimate PPA Market - Based currentValuePerSqft
  [ZooplaPricePerAreaModes.RENT_ESTIMATE]: filter<ZooplaComparableDTO>((x) => !!x.valuation?.rent?.currentValuePerSqft),
  // Valuation PPA Market - Based currentValuePerSqft
  [ZooplaPricePerAreaModes.VAL_ESTIMATE]: filter<ZooplaComparableDTO>(
    (x) => !!x.valuation?.sale?.currentValue && !!x.attributes.floorArea
  ),
};

export const zooplaValueGetters: Record<ZooplaPricePerUnitModes, (comp: ZooplaComparableDTO) => number | undefined> = {
  [ZooplaPricePerUnitModes.VAL_ESTIMATE]: path(["valuation", "sale", "currentValue"]),
  [ZooplaPricePerUnitModes.VAL_LAST]: path(["valuation", "sale", "lastValue"]),
  [ZooplaPricePerUnitModes.RENT_ESTIMATE]: path(["valuation", "rent", "currentValue"]),
  [ZooplaPricePerUnitModes.RENT_ASKING]: path(["valuation", "rent", "lastValue"]),
};

// these don't have optional checks on the assumption that the arrays of items will be filters first
const getValue: Record<ZooplaPriceModes, (transaction: ZooplaComparableDTO) => IValueArea> = {
  [ZooplaPricePerAreaModes.VAL_LAST]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.VAL_LAST](x) as number,
    area: x.attributes.floorArea as number,
  }),
  [ZooplaPricePerUnitModes.VAL_LAST]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.VAL_LAST](x) as number,
    area: x.attributes.floorArea as number,
  }),
  [ZooplaPricePerUnitModes.RENT_ASKING]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.RENT_ASKING](x) as number,
    area: x.attributes.floorArea as number,
  }),
  [ZooplaPricePerUnitModes.RENT_ESTIMATE]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.RENT_ESTIMATE](x) as number,
    area: x.attributes.floorArea as number,
  }),
  [ZooplaPricePerUnitModes.VAL_ESTIMATE]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.VAL_ESTIMATE](x) as number,
    area: x.attributes.floorArea as number,
  }),
  [ZooplaPricePerAreaModes.RENT_ASKING]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.RENT_ASKING](x) as number,
    area: x.attributes.floorArea as number,
  }),
  [ZooplaPricePerAreaModes.RENT_ESTIMATE]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.RENT_ESTIMATE](x) as number,
    area: x.attributes.floorArea as number,
  }),
  [ZooplaPricePerAreaModes.VAL_ESTIMATE]: (x) => ({
    value: zooplaValueGetters[ZooplaPricePerUnitModes.VAL_ESTIMATE](x) as number,
    area: x.attributes.floorArea as number,
  }),
};

/**
 * These only need to be run when in metric mode as zoopla areas are all metric
 */
const manipulateValue: Record<ZooplaPriceModes, (x: IValueArea) => IValueArea> = {
  [ZooplaPricePerUnitModes.RENT_ASKING]: identity,
  [ZooplaPricePerUnitModes.RENT_ESTIMATE]: identity,
  [ZooplaPricePerUnitModes.VAL_ESTIMATE]: identity,
  [ZooplaPricePerUnitModes.VAL_LAST]: identity,
  [ZooplaPricePerAreaModes.RENT_ASKING]: (x) => ({
    value: x.value,
    area: squareFeetToSquareMeters(x.area),
  }),
  [ZooplaPricePerAreaModes.RENT_ESTIMATE]: (x) => ({
    value: x.value,
    area: squareFeetToSquareMeters(x.area),
  }),
  [ZooplaPricePerAreaModes.VAL_ESTIMATE]: (x) => ({
    value: x.value,
    area: squareFeetToSquareMeters(x.area),
  }),
  [ZooplaPricePerAreaModes.VAL_LAST]: (x) => ({
    value: x.value,
    area: squareFeetToSquareMeters(x.area),
  }),
};

/**
 * changes any zoopla price mode to an equivalent last value price mode
 * @param priceMode ZooplaPriceModes
 */
export function getLastValuePriceMode(priceMode: ZooplaPriceModes): ZooplaPriceModes {
  const conversionMap: Record<ZooplaPriceModes, ZooplaPriceModes> = {
    [ZooplaPricePerAreaModes.RENT_ASKING]: ZooplaPricePerAreaModes.RENT_ASKING,
    [ZooplaPricePerAreaModes.RENT_ESTIMATE]: ZooplaPricePerAreaModes.RENT_ASKING,
    [ZooplaPricePerAreaModes.VAL_ESTIMATE]: ZooplaPricePerAreaModes.VAL_LAST,
    [ZooplaPricePerAreaModes.VAL_LAST]: ZooplaPricePerAreaModes.VAL_LAST,
    [ZooplaPricePerUnitModes.RENT_ASKING]: ZooplaPricePerUnitModes.RENT_ASKING,
    [ZooplaPricePerUnitModes.RENT_ESTIMATE]: ZooplaPricePerUnitModes.RENT_ASKING,
    [ZooplaPricePerUnitModes.VAL_ESTIMATE]: ZooplaPricePerUnitModes.VAL_LAST,
    [ZooplaPricePerUnitModes.VAL_LAST]: ZooplaPricePerUnitModes.VAL_LAST,
  };

  return conversionMap[priceMode];
}

/**
 * changes any zoopla price mode to an equivalent last value price mode
 * @param priceMode ZooplaPriceModes
 */
export function getCurrentValuePriceMode(priceMode: ZooplaPriceModes): ZooplaPriceModes {
  const conversionMap: Record<ZooplaPriceModes, ZooplaPriceModes> = {
    [ZooplaPricePerAreaModes.RENT_ASKING]: ZooplaPricePerAreaModes.RENT_ESTIMATE,
    [ZooplaPricePerAreaModes.RENT_ESTIMATE]: ZooplaPricePerAreaModes.RENT_ESTIMATE,
    [ZooplaPricePerAreaModes.VAL_ESTIMATE]: ZooplaPricePerAreaModes.VAL_ESTIMATE,
    [ZooplaPricePerAreaModes.VAL_LAST]: ZooplaPricePerAreaModes.VAL_ESTIMATE,
    [ZooplaPricePerUnitModes.RENT_ASKING]: ZooplaPricePerUnitModes.RENT_ESTIMATE,
    [ZooplaPricePerUnitModes.RENT_ESTIMATE]: ZooplaPricePerUnitModes.RENT_ESTIMATE,
    [ZooplaPricePerUnitModes.VAL_ESTIMATE]: ZooplaPricePerUnitModes.VAL_ESTIMATE,
    [ZooplaPricePerUnitModes.VAL_LAST]: ZooplaPricePerUnitModes.VAL_ESTIMATE,
  };

  return conversionMap[priceMode];
}

/**
 * maps a hometrack price mode to always be a cost/area price mode
 * @param priceMode
 * @returns
 */
export function getPerAreaValueMode(priceMode: ZooplaPriceModes) {
  const conversionMap: Record<ZooplaPriceModes, ZooplaPricePerAreaModes> = {
    [ZooplaPricePerUnitModes.VAL_ESTIMATE]: ZooplaPricePerAreaModes.VAL_ESTIMATE,
    [ZooplaPricePerUnitModes.VAL_LAST]: ZooplaPricePerAreaModes.VAL_LAST,
    [ZooplaPricePerUnitModes.RENT_ASKING]: ZooplaPricePerAreaModes.RENT_ASKING,
    [ZooplaPricePerUnitModes.RENT_ESTIMATE]: ZooplaPricePerAreaModes.RENT_ESTIMATE,
    [ZooplaPricePerAreaModes.RENT_ASKING]: ZooplaPricePerAreaModes.RENT_ASKING,
    [ZooplaPricePerAreaModes.RENT_ESTIMATE]: ZooplaPricePerAreaModes.RENT_ASKING,
    [ZooplaPricePerAreaModes.VAL_ESTIMATE]: ZooplaPricePerAreaModes.VAL_ESTIMATE,
    [ZooplaPricePerAreaModes.VAL_LAST]: ZooplaPricePerAreaModes.VAL_LAST,
  };

  return conversionMap[priceMode];
}

interface IValueArea {
  value: number;
  area: number;
}
export const validValueResults = (
  transactions: ZooplaComparableDTO[],
  priceMode: ZooplaPriceModes,
  mode: CalculatorResultTypes
): ZooplaComparableDTO[] => {
  if (mode === CalculatorResultTypes.ALL) {
    return transactions;
  }
  const valuePriceMode =
    mode === CalculatorResultTypes.SOLD ? getLastValuePriceMode(priceMode) : getCurrentValuePriceMode(priceMode);
  return averagesFilter[valuePriceMode](transactions);
};

const perAreaModes = new Set([
  ZooplaPricePerAreaModes.RENT_ASKING,
  ZooplaPricePerAreaModes.RENT_ESTIMATE,
  ZooplaPricePerAreaModes.VAL_ESTIMATE,
  ZooplaPricePerAreaModes.VAL_LAST,
]);

export const lastValueAverage = (
  transactions: ZooplaComparableDTO[],
  priceMode: ZooplaPriceModes,
  units: MeasurementSystem
): number => {
  const lastValuePriceMode = getLastValuePriceMode(priceMode);
  return pipe<[ZooplaComparableDTO[]], ZooplaComparableDTO[], IValueArea[], IValueArea[], number>(
    // N.B. we use the mapped priceMode to filter to ensure we don't get any NaN or 0 results
    averagesFilter[lastValuePriceMode],
    map(getValue[lastValuePriceMode]),
    units === MeasurementSystem.METRIC ? map(manipulateValue[lastValuePriceMode]) : identity,
    perAreaModes.has(priceMode as ZooplaPricePerAreaModes) ? getMeanPerArea : getMean
  )(transactions);
};

export const currentValueAverage = (
  transactions: ZooplaComparableDTO[],
  priceMode: ZooplaPriceModes,
  units: MeasurementSystem
): number => {
  const currentValuePriceMode = getCurrentValuePriceMode(priceMode);
  return pipe<[ZooplaComparableDTO[]], ZooplaComparableDTO[], IValueArea[], IValueArea[], number>(
    // N.B. we use the mapped priceMode to filter to ensure we don't get any NaN or 0 results
    averagesFilter[currentValuePriceMode],
    map(getValue[currentValuePriceMode]),
    map(units === MeasurementSystem.METRIC ? manipulateValue[currentValuePriceMode] : identity),
    perAreaModes.has(priceMode as ZooplaPricePerAreaModes) ? getMeanPerArea : getMean
  )(transactions);
};
